import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FileCheck2, Send } from "lucide-react";

import { useToast } from "../../components/Toast";
import { Badge, Button } from "../../components/ui";
import { mensajeDeError } from "../../lib/api";
import { getAuth, hasPermission } from "../../lib/auth";
import { emitirSii, type CierreMensualRead } from "../cierres/api";
import {
  enCurso,
  registrarTareaLanzada,
  useTareaCierre,
} from "../cierres/tareas";
import {
  ESTADOS,
  ESTADOS_CON_PROBLEMA,
  ORDEN_ESTADOS,
  useEstadoDte,
} from "./api";

const ESTADOS_EMITIBLES = new Set(["FACTURADO", "HOLD", "CERRADO"]);

/**
 * Documentos tributarios del cierre (DTE C.5): cuántos hay por estado ante el
 * SII y el botón "Emitir al SII". Sólo aparece si el comité emite DTE.
 */
export function DtePanelCierre({
  cierre,
}: {
  cierre: CierreMensualRead;
}): JSX.Element | null {
  const qc = useQueryClient();
  const toast = useToast();
  const auth = getAuth();
  const estadoQ = useEstadoDte(cierre.id);
  const tareaQ = useTareaCierre(cierre.id);

  const lanzar = useMutation({
    mutationFn: () => emitirSii(cierre.id),
    onSuccess: (tarea) => {
      registrarTareaLanzada(qc, tarea);
      void qc.invalidateQueries({ queryKey: ["dte"] });
    },
    onError: (err) =>
      toast.error(mensajeDeError(err, "No se pudo iniciar la emisión al SII")),
  });

  const datos = estadoQ.data;
  if (!datos?.habilitado) return null;

  const conteos = datos.por_estado;
  const porEmitir = (conteos.Borrador ?? 0) + (conteos.Incierto ?? 0);
  const problemas = ESTADOS_CON_PROBLEMA.reduce(
    (n, e) => n + (conteos[e] ?? 0),
    0,
  );
  const puedeEmitir =
    auth != null &&
    hasPermission(auth, "cierres.facturar") &&
    ESTADOS_EMITIBLES.has(cierre.estado) &&
    porEmitir > 0;
  const ocupado = lanzar.isPending || enCurso(tareaQ.data);
  const presentes = ORDEN_ESTADOS.filter((e) => (conteos[e] ?? 0) > 0);

  return (
    <section
      aria-labelledby="dte-cierre-titulo"
      className="space-y-3 rounded-lg border border-neutral-200 bg-white p-4"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2
            id="dte-cierre-titulo"
            className="flex items-center gap-2 font-semibold text-neutral-900"
          >
            <FileCheck2 className="h-4 w-4 text-brand-600" aria-hidden="true" />
            Documentos tributarios (SII)
          </h2>
          <p className="text-sm text-neutral-600">
            {presentes.length === 0
              ? "Las boletas de este cierre todavía no tienen documento tributario."
              : problemas > 0
                ? `${problemas.toLocaleString("es-CL")} documento(s) necesitan revisión.`
                : porEmitir > 0
                  ? `${porEmitir.toLocaleString("es-CL")} boleta(s) listas para enviar al SII.`
                  : "Todas las boletas de este cierre ya salieron al SII."}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            to={`/documentos-sii?cierre_id=${cierre.id}`}
            className="inline-flex items-center rounded-md border border-neutral-300 px-3 py-1.5 text-sm text-neutral-800 hover:bg-neutral-50"
          >
            Ver documentos
          </Link>
          {puedeEmitir && (
            <Button
              icon={Send}
              size="sm"
              disabled={ocupado}
              onClick={() => lanzar.mutate()}
            >
              {ocupado ? "Emitiendo…" : "Emitir al SII"}
            </Button>
          )}
        </div>
      </div>
      {presentes.length > 0 && (
        <ul className="flex flex-wrap gap-2" aria-label="Documentos por estado">
          {presentes.map((e) => (
            <li key={e}>
              <Link
                to={`/documentos-sii?cierre_id=${cierre.id}&estado=${e}`}
                className="rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
              >
                <Badge tone={ESTADOS[e].tono}>
                  {ESTADOS[e].label}:{" "}
                  {(conteos[e] ?? 0).toLocaleString("es-CL")}
                </Badge>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
